import { useMutation, useQueries, useQuery } from '@tanstack/react-query'
import { useOrpcTanstackQueryUtils } from '../orpc'
import { makeThumbnailFile, THUMBNAIL_VARIANTS, type ThumbnailVariant } from '../resize-image'

export function generateS3Key(crewId: string, documentType: string, fileName: string) {
  const timestamp = Date.now()
  const fileExtension = fileName.split('.').pop() || ''
  const baseFileName = fileName.substring(0, fileName.lastIndexOf('.')) || fileName
  const sanitizedFileName = baseFileName.replace(/[^a-z0-9-_]/gi, '_').toLowerCase()

  return `crew/${crewId}/${documentType}/${timestamp}_${sanitizedFileName}.${fileExtension}`
}

export function generateComplianceS3Key(category: string, fileName: string) {
  const timestamp = Date.now()
  const fileExtension = fileName.split('.').pop() || ''
  const baseFileName = fileName.substring(0, fileName.lastIndexOf('.')) || fileName
  const sanitizedFileName = baseFileName.replace(/[^a-z0-9-_]/gi, '_').toLowerCase()

  return `compliance/${category}/${timestamp}_${sanitizedFileName}.${fileExtension}`
}

export function generateAircraftS3Key(aircraftId: string, documentType: string, fileName: string) {
  const timestamp = Date.now()
  const fileExtension = fileName.split('.').pop() || ''
  const baseFileName = fileName.substring(0, fileName.lastIndexOf('.')) || fileName
  const sanitizedFileName = baseFileName.replace(/[^a-z0-9-_]/gi, '_').toLowerCase()

  return `aircraft/${aircraftId}/${documentType}/${timestamp}_${sanitizedFileName}.${fileExtension}`
}

export function generateAircraftPhotoS3Key(aircraftId: string, fileName: string) {
  const timestamp = Date.now()
  const fileExtension = fileName.split('.').pop() || ''
  const baseFileName = fileName.substring(0, fileName.lastIndexOf('.')) || fileName
  const sanitizedFileName = baseFileName.replace(/[^a-z0-9-_]/gi, '_').toLowerCase()

  return `aircraft/${aircraftId}/photo/${timestamp}_${sanitizedFileName}.${fileExtension}`
}

export function generateAircraftFacilityPhotoS3Key(
  aircraftId: string,
  facility: string,
  fileName: string
) {
  const timestamp = Date.now()
  const fileExtension = fileName.split('.').pop() || ''
  const baseFileName = fileName.substring(0, fileName.lastIndexOf('.')) || fileName
  const sanitizedFileName = baseFileName.replace(/[^a-z0-9-_]/gi, '_').toLowerCase()

  return `aircraft/${aircraftId}/facility/${facility}/photo/${timestamp}_${sanitizedFileName}.${fileExtension}`
}

export function generateAircraftFolioScanS3Key(aircraftId: string, fileName: string) {
  const timestamp = Date.now()
  const fileExtension = fileName.split('.').pop() || ''
  const baseFileName = fileName.substring(0, fileName.lastIndexOf('.')) || fileName
  const sanitizedFileName = baseFileName.replace(/[^a-z0-9-_]/gi, '_').toLowerCase()

  return `aircraft/${aircraftId}/folio-scan/${timestamp}_${sanitizedFileName}.${fileExtension}`
}

export function generateCrewPhotoS3Key(crewId: string, fileName: string) {
  const timestamp = Date.now()
  const fileExtension = fileName.split('.').pop() || ''
  const baseFileName = fileName.substring(0, fileName.lastIndexOf('.')) || fileName
  const sanitizedFileName = baseFileName.replace(/[^a-z0-9-_]/gi, '_').toLowerCase()

  return `crew/${crewId}/photo/${timestamp}_${sanitizedFileName}.${fileExtension}`
}

export function generateCrewPreferencePhotoS3Key(
  crewId: string,
  preference: string,
  fileName: string
) {
  const timestamp = Date.now()
  const fileExtension = fileName.split('.').pop() || ''
  const baseFileName = fileName.substring(0, fileName.lastIndexOf('.')) || fileName
  const sanitizedFileName = baseFileName.replace(/[^a-z0-9-_]/gi, '_').toLowerCase()

  return `crew/${crewId}/preference/${preference}/photo/${timestamp}_${sanitizedFileName}.${fileExtension}`
}

export function generatePassengerPhotoS3Key(passengerId: string, fileName: string) {
  const timestamp = Date.now()
  const fileExtension = fileName.split('.').pop() || ''
  const baseFileName = fileName.substring(0, fileName.lastIndexOf('.')) || fileName
  const sanitizedFileName = baseFileName.replace(/[^a-z0-9-_]/gi, '_').toLowerCase()

  return `passenger/${passengerId}/photo/${timestamp}_${sanitizedFileName}.${fileExtension}`
}

export function generatePassengerPreferencePhotoS3Key(
  passengerId: string,
  preference: string,
  fileName: string
) {
  const timestamp = Date.now()
  const fileExtension = fileName.split('.').pop() || ''
  const baseFileName = fileName.substring(0, fileName.lastIndexOf('.')) || fileName
  const sanitizedFileName = baseFileName.replace(/[^a-z0-9-_]/gi, '_').toLowerCase()

  return `passenger/${passengerId}/preference/${preference}/photo/${timestamp}_${sanitizedFileName}.${fileExtension}`
}

export function generateTaskPhotoS3Key(taskId: string, fileName: string) {
  const timestamp = Date.now()
  const fileExtension = fileName.split('.').pop() || ''
  const baseFileName = fileName.substring(0, fileName.lastIndexOf('.')) || fileName
  const sanitizedFileName = baseFileName.replace(/[^a-z0-9-_]/gi, '_').toLowerCase()

  return `task/${taskId}/photo/${timestamp}_${sanitizedFileName}.${fileExtension}`
}

export function generateFlightLogDocumentS3Key(tripLegId: string, fileName: string) {
  const timestamp = Date.now()
  const fileExtension = fileName.split('.').pop() || ''
  const baseFileName = fileName.substring(0, fileName.lastIndexOf('.')) || fileName
  const sanitizedFileName = baseFileName.replace(/[^a-z0-9-_]/gi, '_').toLowerCase()

  return `flight-log/${tripLegId}/${timestamp}_${sanitizedFileName}.${fileExtension}`
}

export function generateChecklistItemPhotoS3Key(taskId: string, itemId: string, fileName: string) {
  const timestamp = Date.now()
  const fileExtension = fileName.split('.').pop() || ''
  const baseFileName = fileName.substring(0, fileName.lastIndexOf('.')) || fileName
  const sanitizedFileName = baseFileName.replace(/[^a-z0-9-_]/gi, '_').toLowerCase()

  return `task/${taskId}/item/${itemId}/photo/${timestamp}_${sanitizedFileName}.${fileExtension}`
}

// Upload a file straight to S3 with a presigned POST. `getUploadPostAsync` is the
// mutateAsync of useS3GetUploadPost; XHR (not fetch) so upload progress is reportable.
export async function uploadFileWithPresignedPost(
  file: File,
  s3Key: string,
  getUploadPostAsync: (input: {
    s3Key: string
    contentType: string
  }) => Promise<{ url: string; fields: Record<string, string> }>,
  onProgress?: (percent: number) => void
): Promise<string> {
  const contentType = file.type || 'application/octet-stream'

  const { url, fields } = await getUploadPostAsync({ s3Key, contentType })

  const formData = new FormData()
  Object.entries(fields).forEach(([key, value]) => {
    formData.append(key, value)
  })
  formData.append('file', file)

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100))
      }
    })

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(s3Key)
      } else {
        reject(new Error(`Failed to upload ${file.name}`))
      }
    })

    xhr.addEventListener('error', () => {
      reject(new Error(`Failed to upload ${file.name}`))
    })

    xhr.open('POST', url)
    xhr.send(formData)
  })
}

export type S3ImageVariant = ThumbnailVariant

export interface S3DownloadUrls {
  downloadUrl: string
  thumbnailUrl?: string
}

// Resized variants live next to the original. Must match `thumbKeyFor` in
// apps/api/src/services/thumbnail.service.ts — the API resolves the variant
// from the original key, the client only needs this to upload it.
export function thumbnailS3KeyFor(s3Key: string, variant: S3ImageVariant = 'thumb'): string {
  return `thumbs/${THUMBNAIL_VARIANTS[variant].size}/${s3Key}.jpg`
}

// Generate a resized JPEG on the device and upload it alongside the original
// so the UI never has to download full-resolution photos for small renders.
// Best-effort: any failure is logged and swallowed — the original upload has
// already succeeded and the UI falls back to it.
export async function uploadThumbnailBestEffort(
  file: File,
  s3Key: string,
  getUploadPostAsync: (input: {
    s3Key: string
    contentType: string
  }) => Promise<{ url: string; fields: Record<string, string> }>,
  variant: S3ImageVariant = 'thumb'
): Promise<void> {
  try {
    const thumb = await makeThumbnailFile(file, variant)
    if (!thumb) return
    const { url, fields } = await getUploadPostAsync({
      s3Key: thumbnailS3KeyFor(s3Key, variant),
      contentType: thumb.type,
    })
    const formData = new FormData()
    Object.entries(fields).forEach(([k, v]) => formData.append(k, v))
    formData.append('file', thumb)
    const res = await fetch(url, { method: 'POST', body: formData })
    if (!res.ok) throw new Error(`Thumbnail upload failed (${res.status})`)
  } catch (err) {
    console.warn('Thumbnail upload skipped:', s3Key, err)
  }
}

// For browser uploads we use the presigned POST flow: get `url` + `fields` to build a FormData.
export function useS3GetUploadPost() {
  const orpc = useOrpcTanstackQueryUtils()

  return useMutation(
    orpc.s3.getUploadPost.mutationOptions({
      onSuccess: () => {
        // No cache invalidation needed for presigned POST data
      },
    })
  )
}

export function useS3GetDownloadUrl() {
  const orpc = useOrpcTanstackQueryUtils()

  return useMutation(
    orpc.s3.getDownloadUrl.mutationOptions({
      onSuccess: () => {
        // No cache invalidation needed for presigned URLs
      },
    })
  )
}

// Query variant for always-visible images (e.g. avatars): TanStack Query dedupes
// per s3Key across the app, and staleTime keeps the URL re-signed before the
// 1-hour signature expires.
//
// `variant` also returns `thumbnailUrl` for the resized copy uploaded next to
// the original ('thumb' 400px square, 'large' fits 1200px); `downloadUrl`
// stays the original for the lightbox.
const DOWNLOAD_URL_STALE_TIME = 45 * 60 * 1000
const DOWNLOAD_URL_GC_TIME = 60 * 60 * 1000

export function useS3DownloadUrlQuery(
  s3Key?: string | null,
  options: { variant?: S3ImageVariant } = {}
) {
  const orpc = useOrpcTanstackQueryUtils()

  return useQuery(
    orpc.s3.getDownloadUrl.queryOptions({
      input: { s3Key: s3Key ?? '', variant: options.variant },
      enabled: !!s3Key,
      staleTime: DOWNLOAD_URL_STALE_TIME,
      gcTime: DOWNLOAD_URL_GC_TIME,
    })
  )
}

// Batch form of useS3DownloadUrlQuery for a list of keys (read-only photo
// collages). Shares the per-key cache with the single-key hook, so a photo
// already resolved in one grid costs nothing in another.
export function useS3DownloadUrlsQuery(
  s3Keys: string[],
  options: { variant?: S3ImageVariant } = {}
): { urls: Record<string, S3DownloadUrls>; isPending: boolean } {
  const orpc = useOrpcTanstackQueryUtils()

  // Cast at the boundary: the app resolves two copies of @tanstack/query-core,
  // so oRPC's queryOptions() and useQueries() disagree on the option type
  // (same pre-existing mismatch as the single-key hooks in this file).
  const queries = s3Keys.map((s3Key) =>
    orpc.s3.getDownloadUrl.queryOptions({
      input: { s3Key, variant: options.variant },
      staleTime: DOWNLOAD_URL_STALE_TIME,
      gcTime: DOWNLOAD_URL_GC_TIME,
    })
  ) as never

  return useQueries({
    queries,
    combine: (results: Array<{ data?: unknown; isPending: boolean }>) => {
      const urls: Record<string, S3DownloadUrls> = {}
      results.forEach((r, i) => {
        const data = r.data as S3DownloadUrls | undefined
        if (data?.downloadUrl) urls[s3Keys[i]] = data
      })
      return { urls, isPending: results.some((r) => r.isPending) }
    },
  })
}
